import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartColumn,
  faCheckSquare,
  faFilePdf,
  faFilter,
  faRotate,
  faSearch,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../../components/Ui_Element/Breadcrumb";
import { showToast } from "../../../../components/Ui_Element/CustomToast";
import config from "../../../../config";
import { getlist } from "../../../../contexts/apicontexts";
import { getCurrentUserIdentity } from "../../ticketUtils";
import { useTheme } from "../../../../contexts/themContext";
import {
  fetchOrganizationCustomerTicketSummary,
  fetchTicketReportStatusOptions,
} from "./ticketSummaryReportService";
import { fetchTicketTaskDetailsReport } from "./ticketTaskDetailsReportService";
import { openTicketTaskDetailsPdfReport } from "./ticketTaskDetailsPdfService";
import { openTicketSummaryPdfReport } from "./ticketSummaryPdfService";

const BRAND_135 = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";
const BRAND_LR  = "linear-gradient(to right, #C04C2A, #4A1B13)";

const toDateInputValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().split("T")[0];
};

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(1);
  return toDateInputValue(date);
};

const getDefaultEndDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1, 0);
  return toDateInputValue(date);
};

const normalizeOrganization = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  name: String(item?.name ?? item?.Name ?? "").trim(),
  organizationCode: String(item?.organizationCode ?? item?.OrganizationCode ?? "").trim(),
  address: String(item?.address ?? item?.Address ?? "").trim(),
  mobile: String(item?.mobile ?? item?.Mobile ?? "").trim(),
  primaryEmail: String(item?.primaryEmail ?? item?.PrimaryEmail ?? "").trim(),
  website: String(item?.website ?? item?.Website ?? "").trim(),
});

const normalizeCustomer = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  name: String(item?.name ?? item?.Name ?? "").trim(),
  customerCode: String(item?.customerCode ?? item?.CustomerCode ?? "").trim(),
  organizationId: Number(item?.organizationId ?? item?.OrganizationId ?? 0),
  organizationName: String(item?.organizationName ?? item?.OrganizationName ?? "").trim(),
  email: String(item?.email ?? item?.Email ?? "").trim(),
  mobile: String(item?.mobile ?? item?.Mobile ?? "").trim(),
  address: String(item?.address ?? item?.Address ?? "").trim(),
  domain: String(item?.domain ?? item?.Domain ?? "").trim(),
  empUserId: String(item?.empUserId ?? item?.EmpUserId ?? "").trim(),
});

const normalizeCustomerUser = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  customerId: Number(item?.customerId ?? item?.CustomerId ?? 0),
  userId: String(item?.userId ?? item?.UserId ?? "").trim(),
  fullName: String(item?.fullName ?? item?.FullName ?? item?.name ?? item?.Name ?? "").trim(),
  email: String(item?.email ?? item?.Email ?? "").trim(),
  customerName: String(item?.customerName ?? item?.CustomerName ?? "").trim(),
  organizationName: String(item?.organizationName ?? item?.OrganizationName ?? "").trim(),
});

const normalizeEmployee = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  userId: String(item?.userId ?? item?.UserId ?? item?.userID ?? item?.UserID ?? "").trim(),
  fullName: String(item?.fullName ?? item?.FullName ?? item?.name ?? item?.Name ?? "").trim(),
  employeeCode: String(item?.employeeCode ?? item?.EmployeeCode ?? "").trim(),
  employeeNumber: String(item?.employeeNumber ?? item?.EmployeeNumber ?? "").trim(),
  workEmail: String(item?.workEmail ?? item?.WorkEmail ?? item?.email ?? item?.Email ?? "").trim(),
});

const getIdentityParts = (nameValue, emailValue, fallback) => ({
  name: String(nameValue ?? "").trim() || fallback,
  email: String(emailValue ?? "").trim(),
});

const formatDateOnly = (value) => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const emptyReport = {
  items: [],
  totalTickets: 0,
  totalOrganizations: 0,
  totalCustomers: 0,
};

const COLUMN_OPTIONS = [
  { key: "ticketName", label: "Ticket Name" },
  { key: "organization", label: "Organization" },
  { key: "employee", label: "Employee" },
  { key: "customer", label: "Customer" },
  { key: "customerUser", label: "Customer User" },
  { key: "openDate", label: "Open Date" },
  { key: "status", label: "Status" },
  { key: "taskCount", label: "Task Count" },
];

const DEFAULT_SELECTED_COLUMNS = COLUMN_OPTIONS.map((item) => item.key);

function ReportField({ label, required = false, children, helper }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {helper ? <div className="mt-2 text-xs text-slate-500">{helper}</div> : null}
    </div>
  );
}

function ColumnMultiSelect({
  options,
  selectedValues,
  onToggle,
  onSelectAll,
  onUnselectAll,
  isOpen,
  onOpenChange,
}) {
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.key))
    .map((option) => option.label)
    .join(", ");

  useEffect(() => {
    if (!isOpen) return undefined;

    const updateMenuPosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const estimatedMenuHeight = 320;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const shouldOpenUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const width = Math.min(Math.max(rect.width, 220), window.innerWidth - 16);
      setMenuStyle({
        position: "fixed",
        top: shouldOpenUp ? "auto" : rect.bottom + 8,
        bottom: shouldOpenUp ? window.innerHeight - rect.top + 8 : "auto",
        left: Math.max(8, rect.right - width),
        width,
        zIndex: 9999,
      });
    };

    updateMenuPosition();

    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) {
        onOpenChange(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, onOpenChange]);

  return (
    <div className="relative z-10">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => onOpenChange(!isOpen)}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 transition hover:border-slate-300"
      >
        <span className="min-w-0 flex-1 truncate text-left">
          {selectedLabels || "Select columns"}
        </span>
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          {selectedValues.length} selected
        </span>
      </button>

      {isOpen && menuStyle ? createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
        >
          <div className="mb-2 flex items-center justify-between gap-2 border-b border-slate-100 px-2 pb-2">
            <button
              type="button"
              onClick={onSelectAll}
              className="rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={onUnselectAll}
              className="rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Unselect All
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto overscroll-contain">
            {options.map((option) => {
              const checked = selectedValues.includes(option.key);

              return (
                <label
                  key={option.key}
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(option.key)}
                    disabled={selectedValues.length === 1 && checked}
                    className="h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                  />
                  <span className="min-w-0 flex-1 break-words">{option.label}</span>
                </label>
              );
            })}
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
}

export default function OrganizationCustomerSummaryReport() {
  const { isDarkMode } = useTheme();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const [organizations, setOrganizations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusOptions, setStatusOptions] = useState(["All"]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [customerUsersLoading, setCustomerUsersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [taskDetailsLoadingTicketId, setTaskDetailsLoadingTicketId] = useState(0);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(emptyReport);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_SELECTED_COLUMNS);
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [filters, setFilters] = useState({
    organizationId: "",
    employeeUserId: "",
    customerId: "",
    customerUserId: "",
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    status: "All",
  });

  useEffect(() => {
    let cancelled = false;

    const loadFilters = async () => {
      setFiltersLoading(true);
      setError("");

      try {
        const [
          organizationResult,
          customerResult,
          employeeResult,
          statusResult,
        ] = await Promise.allSettled([
          getlist(`${config.baseurl}Organizations/getlist`),
          getlist(`${config.baseurl}Customer/getlist`),
          getlist(`${config.baseurl}Employee/getlist`),
          fetchTicketReportStatusOptions(),
        ]);

        if (cancelled) return;

        const failedSources = [];

        if (organizationResult.status === "fulfilled") {
          setOrganizations(Array.isArray(organizationResult.value?.data) ? organizationResult.value.data.map(normalizeOrganization) : []);
        } else {
          failedSources.push("Organizations");
          setOrganizations([]);
        }

        if (customerResult.status === "fulfilled") {
          setCustomers(Array.isArray(customerResult.value?.data) ? customerResult.value.data.map(normalizeCustomer) : []);
        } else {
          failedSources.push("Customers");
          setCustomers([]);
        }

        if (employeeResult.status === "fulfilled") {
          setEmployees(Array.isArray(employeeResult.value?.data) ? employeeResult.value.data.map(normalizeEmployee) : []);
        } else {
          failedSources.push("Employees");
          setEmployees([]);
        }

        if (statusResult.status === "fulfilled") {
          setStatusOptions(["All", ...statusResult.value]);
        } else {
          failedSources.push("Status Options");
          setStatusOptions(["All"]);
        }

        if (failedSources.length > 0) {
          setError(`Report filters could not be fully loaded. Failed: ${failedSources.join(", ")}.`);
        }
      } catch {
        if (!cancelled) {
          setError("Report filters could not be loaded.");
        }
      } finally {
        if (!cancelled) setFiltersLoading(false);
      }
    };

    loadFilters();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedOrganization = useMemo(
    () => organizations.find((item) => Number(item.id) === Number(filters.organizationId || 0)) || null,
    [filters.organizationId, organizations]
  );

  const visibleCustomers = useMemo(() => {
    if (!filters.organizationId) return customers;
    return customers.filter((item) => Number(item.organizationId || 0) === Number(filters.organizationId));
  }, [customers, filters.organizationId]);

  const selectedEmployee = useMemo(
    () => employees.find((item) => item.userId === filters.employeeUserId) || null,
    [employees, filters.employeeUserId]
  );

  const visibleCustomersByEmployee = useMemo(() => {
    if (!filters.employeeUserId) return visibleCustomers;
    return visibleCustomers.filter((item) => String(item.empUserId || "").trim() === filters.employeeUserId);
  }, [filters.employeeUserId, visibleCustomers]);

  const visibleCustomerIdSet = useMemo(
    () => new Set(visibleCustomersByEmployee.map((item) => Number(item.id))),
    [visibleCustomersByEmployee]
  );

  useEffect(() => {
    let cancelled = false;

    const loadCustomerUsers = async () => {
      const scopedCustomerIds = filters.customerId
        ? [Number(filters.customerId)]
        : filters.organizationId || filters.employeeUserId
          ? visibleCustomersByEmployee.map((item) => Number(item.id)).filter((id) => id > 0)
          : [];

      if (scopedCustomerIds.length === 0) {
        setCustomerUsers([]);
        setCustomerUsersLoading(false);
        return;
      }

      setCustomerUsersLoading(true);

      try {
        const responses = await Promise.allSettled(
          scopedCustomerIds.map((customerId) =>
            getlist(`${config.baseurl}CustomerUser/getlist?customerId=${encodeURIComponent(customerId)}`)
          )
        );

        if (cancelled) return;

        const mergedUsers = responses
          .filter((result) => result.status === "fulfilled" && Array.isArray(result.value?.data))
          .flatMap((result) => result.value.data)
          .map(normalizeCustomerUser);

        const uniqueUsers = Array.from(
          new Map(mergedUsers.map((item) => [`${item.id}-${item.userId}`, item])).values()
        );

        setCustomerUsers(uniqueUsers);
      } catch {
        if (!cancelled) {
          setCustomerUsers([]);
        }
      } finally {
        if (!cancelled) {
          setCustomerUsersLoading(false);
        }
      }
    };

    loadCustomerUsers();

    return () => {
      cancelled = true;
    };
  }, [filters.customerId, filters.employeeUserId, filters.organizationId, visibleCustomersByEmployee]);

  const visibleCustomerUsers = useMemo(() => {
    if (filters.customerId) {
      return customerUsers.filter((item) => Number(item.customerId || 0) === Number(filters.customerId));
    }

    if (filters.organizationId || filters.employeeUserId) {
      return customerUsers.filter((item) => visibleCustomerIdSet.has(Number(item.customerId || 0)));
    }

    return customerUsers;
  }, [customerUsers, filters.customerId, filters.employeeUserId, filters.organizationId, visibleCustomerIdSet]);

  const statusTotals = useMemo(() => {
    return report.items.reduce((result, item) => {
      result[item.status] = (result[item.status] || 0) + Number(item.ticketCount || 0);
      return result;
    }, {});
  }, [report.items]);

  const selectedCustomer = useMemo(
    () => customers.find((item) => Number(item.id) === Number(filters.customerId || 0)) || null,
    [customers, filters.customerId]
  );

  const selectedCustomerUser = useMemo(
    () => customerUsers.find((item) => item.userId === filters.customerUserId) || null,
    [customerUsers, filters.customerUserId]
  );

  const reportCompanyInfo = useMemo(() => {
    if (selectedOrganization) {
      return {
        name: selectedOrganization.name || "BanglaFire CRM",
        code: selectedOrganization.organizationCode || "",
        email: selectedOrganization.primaryEmail || selectedCustomer?.email || "support@banglafire.com",
        phone: selectedOrganization.mobile || selectedCustomer?.mobile || "",
        website: selectedOrganization.website || selectedCustomer?.domain || "www.banglafire.com",
        address: selectedOrganization.address || selectedCustomer?.address || "BanglaFire CRM Support Desk",
      };
    }

    if (selectedCustomer) {
      return {
        name: selectedCustomer.name || "BanglaFire CRM",
        code: selectedCustomer.customerCode || "",
        email: selectedCustomer.email || "support@banglafire.com",
        phone: selectedCustomer.mobile || "",
        website: selectedCustomer.domain || "www.banglafire.com",
        address: selectedCustomer.address || "BanglaFire CRM Support Desk",
      };
    }

    return {
      name: "BanglaFire CRM",
      code: "Support Desk",
      email: "support@banglafire.com",
      phone: "",
      website: "www.banglafire.com",
      address: "BanglaFire CRM Support Desk",
    };
  }, [selectedCustomer, selectedOrganization]);

  const reportFilterSummary = useMemo(() => ({
    organizationName: selectedOrganization?.name || "All Organizations",
    employeeName: selectedEmployee?.fullName || selectedEmployee?.workEmail || "All Employees",
    customerName: selectedCustomer?.name || "All Customers",
    customerCode: selectedCustomer?.customerCode || "",
    customerUserName: selectedCustomerUser?.fullName || selectedCustomerUser?.email || "All Customer Users",
    customerUserEmail: selectedCustomerUser?.email || "",
    startDate: filters.startDate,
    endDate: filters.endDate,
    status: filters.status === "All" ? "All Statuses" : filters.status,
  }), [filters.endDate, filters.startDate, filters.status, selectedCustomer, selectedCustomerUser, selectedEmployee, selectedOrganization]);

  useEffect(() => {
    if ((filters.organizationId || filters.employeeUserId) && !visibleCustomersByEmployee.some((item) => Number(item.id) === Number(filters.customerId || 0))) {
      setFilters((current) => ({ ...current, customerId: "", customerUserId: "" }));
      return;
    }

    if (filters.customerUserId && !visibleCustomerUsers.some((item) => item.userId === filters.customerUserId)) {
      setFilters((current) => ({ ...current, customerUserId: "" }));
    }
  }, [filters.organizationId, filters.employeeUserId, filters.customerId, filters.customerUserId, visibleCustomersByEmployee, visibleCustomerUsers]);

  const handleFilterChange = (field, value) => {
    setFilters((current) => {
      if (field === "organizationId") {
        return {
          ...current,
          organizationId: value,
          employeeUserId: "",
          customerId: "",
          customerUserId: "",
        };
      }

      if (field === "employeeUserId") {
        return {
          ...current,
          employeeUserId: value,
          customerId: "",
          customerUserId: "",
        };
      }

      if (field === "customerId") {
        return {
          ...current,
          customerId: value,
          customerUserId: "",
        };
      }

      return {
        ...current,
        [field]: value,
      };
    });
  };

  const validateFilters = () => {
    if (!filters.startDate || !filters.endDate) {
      throw new Error("Start date and end date are required.");
    }

    if (filters.endDate < filters.startDate) {
      throw new Error("End date must be on or after start date.");
    }
  };

  const handleSearch = async () => {
    try {
      validateFilters();
    } catch (validationError) {
      const message = validationError?.message || "Please review the report filters.";
      setError(message);
      showToast.error(message);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await fetchOrganizationCustomerTicketSummary({
        organizationId: filters.organizationId,
        employeeUserId: filters.employeeUserId,
        customerId: filters.customerId,
        customerUserId: filters.customerUserId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: filters.status === "All" ? "" : filters.status,
      });

      setReport(result);
      setSearched(true);
    } catch (requestError) {
      const message = requestError?.message || "Ticket summary report could not be loaded.";
      setError(message);
      setReport(emptyReport);
      setSearched(true);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      await openTicketSummaryPdfReport({
        companyInfo: reportCompanyInfo,
        filters: reportFilterSummary,
        report,
        selectedColumns,
      });
    } catch (exportError) {
      const message = exportError?.message || "PDF report could not be generated.";
      showToast.error(message);
    }
  };

  const handleColumnToggle = (columnKey) => {
    setSelectedColumns((current) => {
      if (current.includes(columnKey)) {
        if (current.length === 1) {
          return current;
        }

        return current.filter((item) => item !== columnKey);
      }

      return [...current, columnKey];
    });
  };

  const handleSelectAllColumns = () => {
    setSelectedColumns(DEFAULT_SELECTED_COLUMNS);
    setIsColumnMenuOpen(false);
  };

  const handleUnselectAllColumns = () => {
    setSelectedColumns([DEFAULT_SELECTED_COLUMNS[0]]);
    setIsColumnMenuOpen(false);
  };

  const isColumnVisible = (columnKey) => selectedColumns.includes(columnKey);

  const handleOpenTaskDetailsReport = async (item) => {
    const ticketId = Number(item?.ticketId || 0);
    const taskCount = Number(item?.taskCount || 0);

    if (ticketId <= 0 || taskCount <= 0) return;

    setTaskDetailsLoadingTicketId(ticketId);

    try {
      const detailReport = await fetchTicketTaskDetailsReport(ticketId);
      await openTicketTaskDetailsPdfReport({
        companyInfo: reportCompanyInfo,
        report: detailReport,
      });
    } catch (reportError) {
      const message = reportError?.message || "Task details report could not be generated.";
      showToast.error(message);
    } finally {
      setTaskDetailsLoadingTicketId(0);
    }
  };

  const inputFocus = (e) => { e.target.style.borderColor = "#C04C2A"; e.target.style.boxShadow = "0 0 0 3px rgba(192,76,42,0.10)"; };
  const inputBlur  = (e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; };

  const statusBadgeCls = (status) => {
    const s = String(status ?? "").trim().toLowerCase();
    if (s === "resolved")    return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
    if (s === "in progress") return "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
    if (s === "on hold")     return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
    if (s === "new")         return "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-700 dark:bg-violet-900/40 dark:text-violet-300";
    if (s === "closed")      return "border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400";
    return "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 p-3 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 md:p-5">
      <Breadcrumb pageName="Ticket Summary Report" currentPage="Ticket Summary Report" />

      <div className="mt-4 overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900" style={{ boxShadow: "0 16px 48px rgba(192,76,42,0.10)" }}>

        {/* ── Hero Header ── */}
        <div
          className="relative overflow-hidden text-white"
          style={{ background: isDarkMode ? "linear-gradient(135deg, #1e293b 0%, #0f172a 55%, #020617 100%)" : BRAND_135 }}
        >
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/5 blur-3xl" />
          <div className="pointer-events-none absolute -left-8 bottom-0 h-32 w-32 rounded-full blur-2xl" style={{ background: "rgba(255,160,100,0.10)" }} />
          <div className="h-[3px] w-full" style={{ background: "linear-gradient(to right, rgba(255,255,255,0.35), rgba(255,255,255,0.05) 60%, transparent)" }} />

          <div className="relative px-5 pb-6 pt-5 sm:px-7">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-orange-100 backdrop-blur-sm">
                  <FontAwesomeIcon icon={faChartColumn} className="text-[8px]" />
                  Support Reporting
                </div>
                <h1 className="mt-2 text-xl font-extrabold tracking-tight text-white drop-shadow-sm sm:text-2xl">
                  Organization &amp; Customer Ticket Summary
                </h1>
                <p className="mt-0.5 text-[11px] text-orange-200">
                  Filter by date, organization, employee, customer, and status to generate a detailed ticket report.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold text-orange-100">
                    {identity.fullName || identity.email || "Current user"}
                  </span>
                  {selectedOrganization ? (
                    <span className="inline-flex items-center rounded-full border border-amber-300/30 bg-amber-400/15 px-3 py-1 text-[10px] font-bold text-amber-100">
                      Org: {selectedOrganization.name}
                    </span>
                  ) : null}
                  {filters.status !== "All" ? (
                    <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold text-orange-100">
                      Status: {filters.status}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading || filtersLoading}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-white/25 bg-white/15 px-4 py-2 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
                  {loading ? "Loading…" : "Run Report"}
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  disabled={loading || filtersLoading}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-white/25 bg-white/15 px-4 py-2 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FontAwesomeIcon icon={faFilePdf} />
                  PDF Report
                </button>
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading || filtersLoading}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                  Refresh
                </button>
              </div>
            </div>

            {Object.keys(statusTotals).length > 0 ? (
              <div className="mt-5 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                {Object.entries(statusTotals).map(([status, count]) => {
                  const s = String(status).trim().toLowerCase();
                  const cardBg =
                    s === "resolved"    ? "rgba(52,211,153,0.18)"  :
                    s === "in progress" ? "rgba(96,165,250,0.18)"  :
                    s === "on hold"     ? "rgba(251,191,36,0.18)"  :
                    s === "new"         ? "rgba(167,139,250,0.18)" :
                                          "rgba(255,255,255,0.10)";
                  const cardBorder =
                    s === "resolved"    ? "rgba(52,211,153,0.35)"  :
                    s === "in progress" ? "rgba(96,165,250,0.35)"  :
                    s === "on hold"     ? "rgba(251,191,36,0.35)"  :
                    s === "new"         ? "rgba(167,139,250,0.35)" :
                                          "rgba(255,255,255,0.18)";
                  return (
                    <div
                      key={status}
                      className="relative overflow-hidden rounded-xl border px-3 py-2.5"
                      style={{ background: cardBg, borderColor: cardBorder }}
                    >
                      <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-orange-100 leading-tight">{status}</div>
                      <div className="mt-1 text-xl font-extrabold leading-none tracking-tight text-white">{count}</div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>

        <div className="space-y-5 p-4 dark:bg-slate-900 md:p-5">

          {/* ── Filters Card ── */}
          <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-base font-extrabold tracking-tight text-slate-900 dark:text-white">Filters</h2>
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white" style={{ background: BRAND_LR }}>
                <FontAwesomeIcon icon={faFilter} className="text-xs" />
              </div>
            </div>

            {error ? (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-300">
                {error}
              </div>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-3">
              <ReportField label="Start Date" required>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(event) => handleFilterChange("startDate", event.target.value)}
                  className="w-full rounded-xl border border-orange-100 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                  onFocus={inputFocus} onBlur={inputBlur}
                />
              </ReportField>

              <ReportField label="End Date" required>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(event) => handleFilterChange("endDate", event.target.value)}
                  className="w-full rounded-xl border border-orange-100 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                  onFocus={inputFocus} onBlur={inputBlur}
                />
              </ReportField>

              <ReportField label="Organization">
                <select
                  value={filters.organizationId}
                  onChange={(event) => handleFilterChange("organizationId", event.target.value)}
                  disabled={filtersLoading}
                  className="w-full rounded-xl border border-orange-100 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition disabled:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:disabled:bg-slate-800"
                  onFocus={inputFocus} onBlur={inputBlur}
                >
                  <option value="">All Organizations</option>
                  {organizations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </ReportField>

              <ReportField label="Employee">
                <select
                  value={filters.employeeUserId}
                  onChange={(event) => handleFilterChange("employeeUserId", event.target.value)}
                  disabled={filtersLoading}
                  className="w-full rounded-xl border border-orange-100 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition disabled:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:disabled:bg-slate-800"
                  onFocus={inputFocus} onBlur={inputBlur}
                >
                  <option value="">All Employees</option>
                  {employees.map((item) => (
                    <option key={item.userId || item.id} value={item.userId}>
                      {item.fullName || item.workEmail || item.userId}
                      {item.employeeCode ? ` (${item.employeeCode})` : item.employeeNumber ? ` (${item.employeeNumber})` : ""}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField label="Customer">
                <select
                  value={filters.customerId}
                  onChange={(event) => handleFilterChange("customerId", event.target.value)}
                  disabled={filtersLoading}
                  className="w-full rounded-xl border border-orange-100 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition disabled:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:disabled:bg-slate-800"
                  onFocus={inputFocus} onBlur={inputBlur}
                >
                  <option value="">{filters.organizationId || filters.employeeUserId ? "All Customers" : "Select Customer"}</option>
                  {visibleCustomersByEmployee.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}{item.customerCode ? ` (${item.customerCode})` : ""}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField label="Customer User">
                <select
                  value={filters.customerUserId}
                  onChange={(event) => handleFilterChange("customerUserId", event.target.value)}
                  disabled={filtersLoading || customerUsersLoading || (!filters.customerId && !filters.organizationId && !filters.employeeUserId)}
                  className="w-full rounded-xl border border-orange-100 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition disabled:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:disabled:bg-slate-800"
                  onFocus={inputFocus} onBlur={inputBlur}
                >
                  <option value="">
                    {!filters.customerId && !filters.organizationId && !filters.employeeUserId
                      ? "Select customer or scope first"
                      : customerUsersLoading ? "Loading customer users…" : "All Customer Users"}
                  </option>
                  {visibleCustomerUsers.map((item) => (
                    <option key={`${item.id}-${item.userId}`} value={item.userId}>
                      {item.fullName || item.email || item.userId}
                      {item.customerName ? ` - ${item.customerName}` : ""}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField label="Status">
                <select
                  value={filters.status}
                  onChange={(event) => handleFilterChange("status", event.target.value)}
                  className="w-full rounded-xl border border-orange-100 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                  onFocus={inputFocus} onBlur={inputBlur}
                >
                  {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </ReportField>

              <button
                type="button"
                onClick={() => {
                  setFilters({ organizationId: "", employeeUserId: "", customerId: "", customerUserId: "", startDate: getDefaultStartDate(), endDate: getDefaultEndDate(), status: "All" });
                  setReport(emptyReport);
                  setError("");
                  setSearched(false);
                }}
                className="rounded-xl border border-orange-100 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-orange-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
              >
                <FontAwesomeIcon icon={faRotate} className="mr-2 text-xs" style={{ color: "#C04C2A" }} />
                Reset Filters
              </button>

              <button
                type="button"
                onClick={handleSearch}
                disabled={loading || filtersLoading}
                className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
                style={{ background: BRAND_LR }}
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
                {loading ? "Loading…" : "Search"}
              </button>
            </div>
          </div>

          {/* ── Ticket Table Card ── */}
          <div className="overflow-visible rounded-2xl border border-orange-100 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="flex flex-col gap-3 border-b border-orange-100 px-5 py-4 dark:border-slate-700 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="text-base font-extrabold tracking-tight text-slate-900 dark:text-white">Ticket Rows</h2>
              <div className="flex items-center justify-end gap-2 lg:w-[280px]">
                <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: BRAND_LR }}>
                  <FontAwesomeIcon icon={faCheckSquare} className="text-xs" />
                </div>
                <div className="min-w-0 flex-1">
                  <ColumnMultiSelect
                    options={COLUMN_OPTIONS}
                    selectedValues={selectedColumns}
                    onToggle={handleColumnToggle}
                    onSelectAll={handleSelectAllColumns}
                    onUnselectAll={handleUnselectAllColumns}
                    isOpen={isColumnMenuOpen}
                    onOpenChange={setIsColumnMenuOpen}
                  />
                </div>
              </div>
            </div>

            {loading ? (
              <div className="px-6 py-16 text-center text-sm dark:text-slate-400" style={{ color: "#C04C2A" }}>
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Loading report rows…
              </div>
            ) : !searched ? (
              <div className="rounded-b-2xl border border-dashed border-orange-200 bg-orange-50/30 px-6 py-14 text-center text-sm text-slate-500 dark:border-slate-600 dark:bg-slate-700/30 dark:text-slate-400">
                Run the report to see ticket rows and task counts.
              </div>
            ) : report.items.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left">
                    <tr style={{ background: isDarkMode ? "linear-gradient(135deg, #1e293b 0%, #0f172a 55%, #020617 100%)" : BRAND_135 }}>
                      {isColumnVisible("ticketName")   ? <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-wider text-orange-100">Ticket Name</th>   : null}
                      {isColumnVisible("organization") ? <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-wider text-orange-100">Organization</th>   : null}
                      {isColumnVisible("employee")     ? <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-wider text-orange-100">Employee</th>       : null}
                      {isColumnVisible("customer")     ? <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-wider text-orange-100">Customer</th>       : null}
                      {isColumnVisible("customerUser") ? <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-wider text-orange-100">Customer User</th>  : null}
                      {isColumnVisible("openDate")     ? <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-wider text-orange-100">Open Date</th>      : null}
                      {isColumnVisible("status")       ? <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-wider text-orange-100">Status</th>         : null}
                      {isColumnVisible("taskCount")    ? <th className="px-5 py-3.5 text-right text-[10px] font-bold uppercase tracking-wider text-orange-100">Tasks</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((item, index) => (
                      <tr
                        key={item.ticketId || `${item.organizationId}-${item.customerId}-${item.customerUserId}-${item.employeeUserId}-${item.ticketName}-${item.openDate}-${item.status}-${index}`}
                        className={`border-t border-orange-50 transition-colors hover:bg-orange-50/50 dark:border-slate-700 dark:hover:bg-slate-700/50 ${index % 2 === 1 ? "bg-orange-50/20 dark:bg-slate-700/20" : "bg-white dark:bg-slate-800"}`}
                      >
                        {isColumnVisible("ticketName")   ? <td className="px-5 py-3 font-medium text-slate-900 dark:text-slate-100">{item.ticketName || "-"}</td> : null}
                        {isColumnVisible("organization") ? <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{item.organizationName}</td> : null}
                        {isColumnVisible("employee")     ? <td className="px-5 py-3">
                          {(() => {
                            const emp = getIdentityParts(item.employeeName, item.employeeEmail, "Unassigned");
                            return (
                              <div className="space-y-0.5">
                                <div className="font-medium text-slate-900 dark:text-slate-100">{emp.name}</div>
                                {emp.email ? <div className="text-xs text-slate-500 dark:text-slate-400">{emp.email}</div> : null}
                              </div>
                            );
                          })()}
                        </td> : null}
                        {isColumnVisible("customer")     ? <td className="px-5 py-3 font-semibold text-slate-900 dark:text-slate-100">{item.customerName}</td> : null}
                        {isColumnVisible("customerUser") ? <td className="px-5 py-3">
                          {(() => {
                            const cu = getIdentityParts(item.customerUserName, item.customerUserEmail, "Unknown User");
                            return (
                              <div className="space-y-0.5">
                                <div className="font-medium text-slate-900 dark:text-slate-100">{cu.name}</div>
                                {cu.email ? <div className="text-xs text-slate-500 dark:text-slate-400">{cu.email}</div> : null}
                              </div>
                            );
                          })()}
                        </td> : null}
                        {isColumnVisible("openDate")  ? <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{formatDateOnly(item.openDate)}</td> : null}
                        {isColumnVisible("status")    ? <td className="px-5 py-3">
                          <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusBadgeCls(item.status)}`}>
                            {item.status}
                          </span>
                        </td> : null}
                        {isColumnVisible("taskCount") ? <td className="px-5 py-3 text-right font-semibold text-slate-900 dark:text-slate-100">
                          {Number(item.taskCount || 0) > 0 ? (
                            <button
                              type="button"
                              onClick={() => handleOpenTaskDetailsReport(item)}
                              disabled={taskDetailsLoadingTicketId === Number(item.ticketId || 0)}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-bold transition hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-70 dark:border-orange-800 dark:bg-orange-900/30 dark:text-orange-300 dark:hover:bg-orange-900/50"
                              style={{ color: "#C04C2A" }}
                            >
                              {taskDetailsLoadingTicketId === Number(item.ticketId || 0) ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                              {item.taskCount} {Number(item.taskCount) === 1 ? "Task" : "Tasks"}
                            </button>
                          ) : (
                            <span className="text-slate-400 dark:text-slate-500">0</span>
                          )}
                        </td> : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-b-2xl border border-dashed border-orange-200 bg-orange-50/20 px-6 py-14 text-center dark:border-slate-600 dark:bg-slate-700/20">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: BRAND_LR }}>
                  <FontAwesomeIcon icon={faChartColumn} />
                </div>
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">No tickets matched the selected filters.</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
